import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router, RouterModule } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';
import { UserUtil } from 'app/modules/shared/utils/user.util';
import { TranslocoModule } from '@ngneat/transloco';
import { AppParameterService } from 'app/modules/shared/services/appParameter.service';
import { AppParameter } from 'app/modules/shared/models/appParameter';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    standalone: true,
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
        TranslocoModule,
        CommonModule,
        RouterModule
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    
    PathEnum = PathEnum;

    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;
    appParameter: AppParameter;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _appParameterService: AppParameterService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to app parameter changes
        this._appParameterService.appParameter$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((appParameter: AppParameter) => {
                this.appParameter = appParameter;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        // this._userService.update({
        //     ...this.user,
        //     status
        // }).subscribe();
    }

    /**
     * Check if user is partner super admin
     */
    isPartnerSuperAdmin(): boolean {
        return this.user && UserUtil.isPartnerSuperAdmin(this.user);
    }

    /**
     * Sign out — route to the user-type-specific sign-out page so each auth chain
     * (PartnerUser / TeamUser / AffiliateUser) clears its own JWT + storage.
     */
    signOut(): void {
        if(UserUtil.isPartnerUser(this.user)){
            this._router.navigate([PathEnum.SESSION, PathEnum.SESSION_SIGN_OUT]);
        } else if(UserUtil.isTeamUser(this.user)) {
            this._router.navigate([PathEnum.APP_TEAM, PathEnum.SESSION, PathEnum.SESSION_SIGN_OUT]);
        } else if(UserUtil.isAffiliateUser(this.user)) {
            this._router.navigate([PathEnum.APP_AFFILIATE, PathEnum.SESSION, PathEnum.APP_AFFILIATE_SIGN_OUT]);
        } else {
            console.error("Unknown user type")
            this._router.navigate([PathEnum.SESSION, PathEnum.SESSION_SIGN_OUT]);
        }
    }
}